import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import { assertNever } from 'src/queries/agent/network/agent.network'
import { BackendErrorPayload } from 'src/utils/error.utils'

export type DropdownValue<T = unknown> = {
  value: string
  label?: string
  details?: T
  selected?: boolean
}

// State machine types
export type DropdownState<T> = {
  search: string
  searchPending: boolean
  initialized: boolean
  focusRequest: 'none' | 'handle' | 'search'
  lastUserAction: 'none' | 'select' | 'search' | 'cancel' | 'open'
  optimisticValues: DropdownValue<T>[] // For multi-select optimistic updates
  asyncState: LoadingState
  asyncOperationId: string | undefined
  asyncError: BackendErrorPayload<string> | undefined
  multiSelectMode: boolean
}

export type DropdownAction<T> =
  | { type: 'SEARCH_CHANGED'; payload: string }
  | { type: 'SEARCH_PENDING_STARTED' }
  | { type: 'SEARCH_PENDING_COMPLETED' }
  | { type: 'INITIALIZED' }
  | { type: 'RESET' }
  | { type: 'REQUEST_FOCUS'; target: 'handle' | 'search' }
  | { type: 'FOCUS_HANDLED' }
  | { type: 'USER_ACTION'; action: 'select' | 'search' | 'cancel' | 'open' }
  | {
      type: 'SELECT_ASYNC'
      payload: {
        selectedValue: DropdownValue<T>
        operationId: string
      }
    }
  | { type: 'ASYNC_COMPLETED'; payload: { operationId: string } }
  | { type: 'ASYNC_FAILED'; payload: { operationId: string; error?: any } }
  | { type: 'CLEAR_OPTIMISTIC' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_SELECTIONS' }

// State machine reducer
export const dropdownReducer = <T>(
  state: DropdownState<T>,
  action: DropdownAction<T>,
): DropdownState<T> => {
  switch (action.type) {
    case 'SEARCH_CHANGED':
      return {
        ...state,
        search: action.payload,
        lastUserAction:
          action.payload !== state.search ? 'search' : state.lastUserAction,
      }
    case 'SEARCH_PENDING_STARTED':
      return { ...state, searchPending: true }
    case 'SEARCH_PENDING_COMPLETED':
      return { ...state, searchPending: false }
    case 'INITIALIZED':
      return { ...state, initialized: true }
    case 'RESET':
      return {
        ...state,
        search: '',
        searchPending: false,
        initialized: false,
        focusRequest: 'none',
        lastUserAction: 'none',
        optimisticValues: [],
        asyncState: 'idle',
        asyncOperationId: undefined,
        asyncError: undefined,
      }
    case 'REQUEST_FOCUS':
      return { ...state, focusRequest: action.target }
    case 'FOCUS_HANDLED':
      return { ...state, focusRequest: 'none' }
    case 'USER_ACTION':
      return {
        ...state,
        lastUserAction: action.action,
        focusRequest:
          // Only immediate focus for cancel, not select (select waits for loading completion)
          action.action === 'cancel' ? 'handle' : state.focusRequest,
      }
    case 'SELECT_ASYNC': {
      const optimisticValues: DropdownValue<T>[] = []

      if (state.multiSelectMode) {
        optimisticValues.push(...state.optimisticValues)

        const index = optimisticValues.findIndex(
          (v) => v.value === action.payload.selectedValue.value,
        )

        if (index !== -1) {
          // Deselect if already selected
          optimisticValues.splice(index, 1)
        } else {
          // Add to selections
          optimisticValues.push(action.payload.selectedValue)
        }
      } else {
        optimisticValues.push(action.payload.selectedValue)
      }

      return {
        ...state,
        optimisticValues,
        asyncState: 'pending',
        asyncOperationId: action.payload.operationId,
        lastUserAction: 'select',
        // Clear any previous error when making a new selection
        asyncError: undefined,
      }
    }
    case 'ASYNC_COMPLETED':
      if (
        action.payload.operationId !== state.asyncOperationId &&
        action.payload.operationId !== 'external'
      ) {
        return state // Ignore outdated operations, but allow external operations
      }
      return {
        ...state,
        optimisticValues: [],
        asyncState: 'success',
        asyncOperationId: undefined,
        focusRequest: 'handle', // Focus after async completion
      }
    case 'ASYNC_FAILED':
      if (
        action.payload.operationId !== state.asyncOperationId &&
        action.payload.operationId !== 'external'
      ) {
        return state // Ignore outdated operations, but allow external operations
      }

      return {
        ...state,
        optimisticValues: [],
        asyncState: 'error',
        asyncOperationId: undefined,
        focusRequest: 'handle', // Focus after async failure
        // Store the error information
        asyncError: action.payload.error || {
          error: 'An error occurred during selection',
        },
      }
    case 'CLEAR_OPTIMISTIC':
      return {
        ...state,
        optimisticValues: [],
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        asyncError: undefined,
        asyncState: state.asyncState === 'error' ? 'idle' : state.asyncState,
      }
    case 'CLEAR_SELECTIONS':
      return {
        ...state,
        optimisticValues: [],
      }
    default:
      assertNever(action)
  }
}

export const initialDropdownState = <T>(): DropdownState<T> => ({
  search: '',
  searchPending: false,
  initialized: false,
  focusRequest: 'none',
  lastUserAction: 'none',
  optimisticValues: [],
  asyncState: 'idle',
  asyncOperationId: undefined,
  asyncError: undefined,
  multiSelectMode: false,
})
